import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Refresh,
  Assignment,
  Timeline,
  AttachMoney,
  CalendarToday,
  Person,
  CheckCircle,
  Pending,
  Warning,
  Code as TechnologyIcon,
} from '@mui/icons-material';
import { projectAPI, taskAPI } from '../services/api';
import { Project, Task } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_type: 'web',
    status: 'planning',
    client: 0,
    project_manager: 0,
    start_date: '',
    end_date: '',
    budget: 0,
    technologies: '',
  });

  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    project: 0,
    assigned_to: 0,
    priority: 'medium',
    status: 'todo',
    due_date: '',
    estimated_hours: 0,
    actual_hours: 0,
  });

  useEffect(() => {
    fetchProjects();
    fetchTasks();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      showSnackbar('Failed to load projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await taskAPI.getAll();
      setTasks(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmitProject = async () => {
    try {
      const projectData = {
        ...formData,
        budget: formData.budget,
      };
      if (editingProject) {
        await projectAPI.update(editingProject.id, projectData);
        showSnackbar('Project updated successfully', 'success');
      } else {
        await projectAPI.create(projectData);
        showSnackbar('Project created successfully', 'success');
      }
      fetchProjects();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving project:', error);
      showSnackbar('Failed to save project', 'error');
    }
  };

  const handleDeleteProject = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this project? This will also delete all associated tasks.')) {
      try {
        await projectAPI.delete(id);
        showSnackbar('Project deleted successfully', 'success');
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        showSnackbar('Failed to delete project', 'error');
      }
    }
  };

  const handleSubmitTask = async () => {
    try {
      if (editingTask) {
        await taskAPI.update(editingTask.id, taskFormData);
        showSnackbar('Task updated successfully', 'success');
      } else {
        await taskAPI.create(taskFormData);
        showSnackbar('Task created successfully', 'success');
      }
      fetchTasks();
      handleCloseTaskDialog();
    } catch (error) {
      console.error('Error saving task:', error);
      showSnackbar('Failed to save task', 'error');
    }
  };

  const handleDeleteTask = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await taskAPI.delete(id);
        showSnackbar('Task deleted successfully', 'success');
        fetchTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
        showSnackbar('Failed to delete task', 'error');
      }
    }
  };

  const handleOpenDialog = (project?: Project) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name,
        description: project.description,
        project_type: project.project_type,
        status: project.status,
        client: project.client,
        project_manager: project.project_manager,
        start_date: project.start_date,
        end_date: project.end_date || '',
        budget: project.budget || 0,
        technologies: project.technologies,
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        description: '',
        project_type: 'web',
        status: 'planning',
        client: 0,
        project_manager: 0,
        start_date: '',
        end_date: '',
        budget: 0,
        technologies: '',
      });
    }
    setOpenDialog(true);
  };

  const handleOpenTaskDialog = (task?: Task, projectId?: number) => {
    if (task) {
      setEditingTask(task);
      setTaskFormData({
        title: task.title,
        description: task.description,
        project: task.project,
        assigned_to: task.assigned_to,
        priority: task.priority,
        status: task.status,
        due_date: task.due_date,
        estimated_hours: task.estimated_hours,
        actual_hours: task.actual_hours,
      });
    } else {
      setEditingTask(null);
      setTaskFormData({
        title: '',
        description: '',
        project: projectId || 0,
        assigned_to: 0,
        priority: 'medium',
        status: 'todo',
        due_date: '',
        estimated_hours: 0,
        actual_hours: 0,
      });
    }
    setOpenTaskDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProject(null);
  };

  const handleCloseTaskDialog = () => {
    setOpenTaskDialog(false);
    setEditingTask(null);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handleTypeFilterChange = (event: SelectChangeEvent) => {
    setTypeFilter(event.target.value);
  };

  const handleFormProjectTypeChange = (event: SelectChangeEvent) => {
    setFormData({ ...formData, project_type: event.target.value });
  };

  const handleFormStatusChange = (event: SelectChangeEvent) => {
    setFormData({ ...formData, status: event.target.value });
  };

  const handleTaskPriorityChange = (event: SelectChangeEvent) => {
    setTaskFormData({ ...taskFormData, priority: event.target.value });
  };

  const handleTaskStatusChange = (event: SelectChangeEvent) => {
    setTaskFormData({ ...taskFormData, status: event.target.value });
  };

  const getStatusColor = (status: string): any => {
    const colors: Record<string, any> = {
      planning: 'default',
      in_progress: 'primary',
      review: 'warning',
      completed: 'success',
      maintenance: 'info',
    };
    return colors[status];
  };

  const getPriorityColor = (priority: string): any => {
    const colors: Record<string, any> = {
      low: 'success',
      medium: 'info',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority];
  };

  const getTaskStatusColor = (status: string): any => {
    const colors: Record<string, any> = {
      todo: 'default',
      in_progress: 'primary',
      review: 'warning',
      done: 'success',
    };
    return colors[status];
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesType = typeFilter === 'all' || project.project_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getProjectTasks = (projectId: number) => {
    return tasks.filter(task => task.project === projectId);
  };

  const getProjectProgress = (projectId: number) => {
    const projectTasks = getProjectTasks(projectId);
    if (projectTasks.length === 0) return 0;
    const completedTasks = projectTasks.filter(task => task.status === 'done').length;
    return (completedTasks / projectTasks.length) * 100;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        Project Management
      </Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Projects List" />
        <Tab label="Tasks Board" />
        <Tab label="Analytics" />
      </Tabs>

      {tabValue === 0 && (
        <>
          {/* Filters and Actions */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="planning">Planning</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="review">Review</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type Filter</InputLabel>
                  <Select
                    value={typeFilter}
                    label="Type Filter"
                    onChange={handleTypeFilterChange}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="web">Web Development</MenuItem>
                    <MenuItem value="mobile">Mobile Development</MenuItem>
                    <MenuItem value="integration">Integration</MenuItem>
                    <MenuItem value="consultancy">Consultancy</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                >
                  New Project
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Projects Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell>Tasks</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography sx={{ py: 4 }}>
                        No projects found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProjects.map((project) => (
                    <TableRow key={project.id} hover>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {project.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={project.project_type} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={project.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(project.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={getProjectProgress(project.id)}
                            sx={{ flex: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">
                            {getProjectProgress(project.id).toFixed(0)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{format(new Date(project.start_date), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        {project.end_date ? format(new Date(project.end_date), 'MMM dd, yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell>
                        ${project.budget?.toLocaleString() || '0'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${getProjectTasks(project.id).length} tasks`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              setSelectedProject(project);
                              setOpenDetails(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add Task">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenTaskDialog(undefined, project.id)}
                          >
                            <Assignment />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(project)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tabValue === 1 && (
        <>
          {/* Tasks Board */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Tasks Board</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenTaskDialog()}>
                New Task
              </Button>
            </Box>

            <Grid container spacing={2}>
              {['todo', 'in_progress', 'review', 'done'].map((status) => (
                <Grid size={{ xs: 12, md: 3 }} key={status}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: '#f8f9fa',
                      minHeight: 400,
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        textAlign: 'center',
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          status === 'todo' ? '#e3f2fd' :
                          status === 'in_progress' ? '#fff3e0' :
                          status === 'review' ? '#f3e5f5' : '#e8f5e8',
                      }}
                    >
                      {status === 'todo' && '📋 To Do'}
                      {status === 'in_progress' && '🔄 In Progress'}
                      {status === 'review' && '👀 In Review'}
                      {status === 'done' && '✅ Done'}
                      <Chip
                        label={tasks.filter(t => t.status === status).length}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {tasks
                        .filter(task => task.status === status)
                        .map((task) => (
                          <Card key={task.id} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => handleOpenTaskDialog(task)}>
                            <CardContent>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {task.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                {task.description.substring(0, 60)}...
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                <Chip
                                  label={task.priority}
                                  size="small"
                                  color={getPriorityColor(task.priority)}
                                />
                                <Typography variant="caption">
                                  Due: {format(new Date(task.due_date), 'MMM dd')}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption">
                                  📊 {task.actual_hours}/{task.estimated_hours}h
                                </Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTask(task.id);
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </>
      )}

      {tabValue === 2 && (
        <>
          {/* Analytics Dashboard */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircle sx={{ color: '#4caf50', mr: 1 }} />
                    <Typography variant="h6">Completion Rate</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {projects.length > 0
                      ? ((projects.filter(p => p.status === 'completed').length / projects.length) * 100).toFixed(0)
                      : 0}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Projects completed successfully
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Pending sx={{ color: '#ff9800', mr: 1 }} />
                    <Typography variant="h6">Active Projects</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {projects.filter(p => p.status === 'in_progress').length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Currently in progress
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Assignment sx={{ color: '#2196f3', mr: 1 }} />
                    <Typography variant="h6">Total Tasks</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {tasks.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Tasks across all projects
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AttachMoney sx={{ color: '#4caf50', mr: 1 }} />
                    <Typography variant="h6">Total Budget</Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    ${projects.reduce((sum, p) => sum + (p.budget || 0), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Across all projects
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Project Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProject ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={formData.project_type}
                  label="Project Type"
                  onChange={handleFormProjectTypeChange}
                >
                  <MenuItem value="web">Web Development</MenuItem>
                  <MenuItem value="mobile">Mobile Development</MenuItem>
                  <MenuItem value="integration">Software Integration</MenuItem>
                  <MenuItem value="consultancy">IT Consultancy</MenuItem>
                  <MenuItem value="custom">Custom Development</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleFormStatusChange}
                >
                  <MenuItem value="planning">Planning</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="review">Under Review</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Budget"
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) })}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Technologies"
                placeholder="React, Django, PostgreSQL"
                value={formData.technologies}
                onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmitProject} variant="contained">
            {editingProject ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={openTaskDialog} onClose={handleCloseTaskDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Create New Task'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Task Title"
                value={taskFormData.title}
                onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={taskFormData.description}
                onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={taskFormData.due_date}
                onChange={(e) => setTaskFormData({ ...taskFormData, due_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={taskFormData.priority}
                  label="Priority"
                  onChange={handleTaskPriorityChange}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={taskFormData.status}
                  label="Status"
                  onChange={handleTaskStatusChange}
                >
                  <MenuItem value="todo">To Do</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="review">In Review</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Estimated Hours"
                type="number"
                value={taskFormData.estimated_hours}
                onChange={(e) => setTaskFormData({ ...taskFormData, estimated_hours: parseInt(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTaskDialog}>Cancel</Button>
          <Button onClick={handleSubmitTask} variant="contained">
            {editingTask ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Project Details</DialogTitle>
        <DialogContent>
          {selectedProject && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                {selectedProject.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedProject.description}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Timeline sx={{ mr: 1, color: '#1976d2' }} />
                    <Typography variant="body2">
                      <strong>Timeline:</strong> {format(new Date(selectedProject.start_date), 'MMM dd, yyyy')} -{' '}
                      {selectedProject.end_date ? format(new Date(selectedProject.end_date), 'MMM dd, yyyy') : 'Ongoing'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AttachMoney sx={{ mr: 1, color: '#4caf50' }} />
                    <Typography variant="body2">
                      <strong>Budget:</strong> ${selectedProject.budget?.toLocaleString() || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TechnologyIcon sx={{ mr: 1, color: '#ff9800' }} />
                    <Typography variant="body2">
                      <strong>Technologies:</strong> {selectedProject.technologies}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ mr: 1, color: '#9c27b0' }} />
                    <Typography variant="body2">
                      <strong>Client ID:</strong> {selectedProject.client}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Tasks ({getProjectTasks(selectedProject.id).length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getProjectTasks(selectedProject.id).map((task) => (
                      <TableRow key={task.id}>
                        <TableCell>{task.title}</TableCell>
                        <TableCell>
                          <Chip label={task.priority} size="small" color={getPriorityColor(task.priority)} />
                        </TableCell>
                        <TableCell>
                          <Chip label={task.status} size="small" color={getTaskStatusColor(task.status)} />
                        </TableCell>
                        <TableCell>{format(new Date(task.due_date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>
                          <LinearProgress
                            variant="determinate"
                            value={task.estimated_hours > 0 ? (task.actual_hours / task.estimated_hours) * 100 : 0}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Projects;